// iter55 · Unified Contractor Setup (P1 foundation slice).
//
// Single setup journey at ``/app/setup`` that supersedes the two legacy
// entry points (``/app/quick-start`` and ``/app/pricing-profile/wizard``).
// The permanent Settings pages (Company Profile, Pricing Profile, Document
// Defaults, Closeout Defaults) remain the single source of truth — this
// page just orchestrates them in one guided flow.
//
// Sections (hash-anchored so deep links keep working):
//   1. Company Basics       #company        →  PUT /api/company-profile
//   2. Trades               #trades         →  PUT /api/company-profile
//   3. Pricing Basics       #pricing-basics →  PUT /api/pricing-profile
//   4. Trade / System Pricing #trade-pricing →  PUT /api/pricing-profile
//   5. Document Defaults    #documents      →  PUT /api/document-defaults
//   6. Closeout Defaults    #closeout       →  PUT /api/closeout-defaults
//   7. Readiness Review     #review         →  GET /api/onboarding-status
//
// Save-and-resume: each section save PATCHes
// /api/pricing-profile/setup-progress with ``section`` so re-opening the
// page returns the contractor to their last section.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, explainSaveError } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import {
  ArrowLeft, ArrowRight, Building2, ClipboardCheck, FileText, Hammer,
  Loader2, ShieldCheck, Sparkles, Wallet, ListChecks, Check, AlertTriangle,
  Save, Plus, Trash2,
} from "lucide-react";
import { TIME_ZONE_OPTIONS, DEFAULT_TIME_ZONE, detectBrowserTimeZone } from "@/lib/timeZones";

const SECTIONS = [
  { id: "company",        title: "Company Basics",        icon: Building2,     doneKey: "company_profile" },
  { id: "trades",         title: "Trades",                icon: Hammer,        doneKey: "trades" },
  { id: "pricing-basics", title: "Pricing Basics",        icon: Wallet,        doneKey: "pricing_basics" },
  { id: "trade-pricing",  title: "Trade / System Pricing", icon: ListChecks,   doneKey: "trade_pricing" },
  { id: "documents",      title: "Document Defaults",     icon: FileText,      doneKey: "document_defaults" },
  { id: "closeout",       title: "Closeout Defaults",     icon: ClipboardCheck, doneKey: "closeout_defaults" },
  { id: "review",         title: "Readiness Review",      icon: ShieldCheck,   doneKey: "review" },
];

const SECTION_INDEX = Object.fromEntries(SECTIONS.map((s, i) => [s.id, i]));
const DEFAULT_SECTION = "company";

const hashToSection = (hash) => {
  const key = String(hash || "").replace(/^#/, "").toLowerCase();
  return SECTION_INDEX[key] !== undefined ? key : DEFAULT_SECTION;
};

// ---------- shared field bits (kept lightweight; no external deps) ----------
const FieldLabel = ({ children, hint }) => (
  <>
    <label className="block text-xs font-semibold text-[var(--jc-text-soft)] mb-1.5">{children}</label>
    {hint && <p className="text-xs text-[var(--jc-text-soft)] mb-1.5 -mt-1">{hint}</p>}
  </>
);
const TextInput = (props) => (
  <input
    {...props}
    className={`w-full px-3.5 py-2.5 rounded-md border border-[var(--jc-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)] ${props.className || ""}`}
  />
);
const TextArea = (props) => (
  <textarea
    rows={3}
    {...props}
    className={`w-full px-3.5 py-2.5 rounded-md border border-[var(--jc-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)] ${props.className || ""}`}
  />
);
const NumberInput = ({ suffix, ...rest }) => (
  <div className="flex items-stretch">
    <input
      {...rest}
      type="number"
      className="w-full px-3.5 py-2.5 rounded-l-md border border-[var(--jc-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)]"
    />
    {suffix && (
      <span className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-[var(--jc-border-strong)] bg-[var(--jc-bg)] text-sm text-[var(--jc-text-soft)]">
        {suffix}
      </span>
    )}
  </div>
);

// ---------- data model ----------
const emptyState = {
  company: {
    company_name: "", primary_trade: "", trade_categories: [],
    phone: "", email: "", service_area_notes: "", time_zone: "",
  },
  pricing: {
    default_pricing_style: "hybrid",
    labor_rate: 65, markup_pct: 25, profit_margin_pct: 10,
    tax_rate: 8, trip_charge: 0, deposit_pct: 50,
    material_rules: [],
  },
  documents: {
    proposal_intro: "", proposal_footer: "",
    deposit_terms: "", final_payment_terms: "", late_payment_note: "",
    contract_terms: "", contractor_disclaimer: "", customer_approval_language: "",
    invoice_notes: "", payment_instructions: "",
    customer_update_signature: "", review_request: "", referral_request: "",
  },
  closeout: {
    workmanship_warranty_terms: "", manufacturer_warranty_notes: "",
    maintenance_notes: "", warranty_contact_notes: "",
    closeout_notes_template: "", review_request: "", referral_request: "",
  },
};

const clone = (v) => JSON.parse(JSON.stringify(v));

const Setup = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [section, setSection] = useState(() => hashToSection(location.hash));
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [company, setCompany] = useState(emptyState.company);
  const [tradeChoices, setTradeChoices] = useState([]);
  const [pricing, setPricing] = useState(emptyState.pricing);
  const [documents, setDocuments] = useState(emptyState.documents);
  const [closeout, setCloseout] = useState(emptyState.closeout);
  const [onboarding, setOnboarding] = useState(null);

  const bootstrap = useCallback(async () => {
    setLoading(true);
    const [cp, pp, dd, cd, tc, ob] = await Promise.all([
      api.get("/company-profile").catch(() => ({ data: null })),
      api.get("/pricing-profile").catch(() => ({ data: null })),
      api.get("/document-defaults").catch(() => ({ data: null })),
      api.get("/closeout-defaults").catch(() => ({ data: null })),
      api.get("/company-profile/trade-choices").catch(() => ({ data: null })),
      api.get("/onboarding-status").catch(() => ({ data: null })),
    ]);
    if (cp.data) {
      setCompany({
        company_name: cp.data.company_name || "",
        primary_trade: cp.data.primary_trade || "",
        trade_categories: cp.data.trade_categories || [],
        phone: cp.data.phone || "",
        email: cp.data.email || "",
        service_area_notes: cp.data.service_area_notes || "",
        time_zone: cp.data.time_zone || detectBrowserTimeZone(),
      });
    } else {
      setCompany((prev) => ({ ...prev, time_zone: detectBrowserTimeZone() }));
    }
    if (pp.data) {
      setPricing({
        default_pricing_style: pp.data.default_pricing_style || "hybrid",
        labor_rate: pp.data.labor_rate ?? 65,
        markup_pct: pp.data.markup_pct ?? 25,
        profit_margin_pct: pp.data.profit_margin_pct ?? 10,
        tax_rate: pp.data.tax_rate ?? 8,
        trip_charge: pp.data.trip_charge ?? 0,
        deposit_pct: pp.data.deposit_pct ?? 50,
        material_rules: pp.data.material_rules || [],
      });
    }
    if (dd.data) setDocuments((prev) => ({ ...prev, ...dd.data }));
    if (cd.data) setCloseout((prev) => ({ ...prev, ...cd.data }));
    // iter59 · Slice 2A — trade-choices endpoint now returns a plain array.
    // Keep a defensive fallback for legacy responses so old cached bundles
    // don't crash between deploys.
    const choices = Array.isArray(tc.data) ? tc.data : (tc.data?.choices || []);
    if (choices.length) setTradeChoices(choices);
    if (ob.data) {
      setOnboarding(ob.data);
      // First-load resume: honor URL hash first, otherwise use
      // ``setup_last_section`` from the server.
      const hashSection = String(location.hash || "").replace(/^#/, "").toLowerCase();
      if (!hashSection && ob.data.setup_last_section) {
        const s = String(ob.data.setup_last_section).toLowerCase();
        if (SECTION_INDEX[s] !== undefined) {
          setSection(s);
          navigate({ hash: `#${s}` }, { replace: true });
        }
      }
    }
    setLoading(false);
  }, [location.hash, navigate]);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  // Persist section anchor to server so a device switch also resumes here.
  const persistSection = useCallback(async (id) => {
    try { await api.patch("/pricing-profile/setup-progress", { step: 0, section: id }); } catch { /* non-fatal */ }
  }, []);

  const goto = (id) => {
    if (!SECTION_INDEX[id] && id !== undefined) return;
    setSection(id);
    navigate({ hash: `#${id}` }, { replace: true });
    persistSection(id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const gotoNext = () => {
    const i = SECTION_INDEX[section];
    if (i < SECTIONS.length - 1) goto(SECTIONS[i + 1].id);
  };
  const gotoPrev = () => {
    const i = SECTION_INDEX[section];
    if (i > 0) goto(SECTIONS[i - 1].id);
  };

  // ---------- Save handlers (each hits the permanent settings record) ----------
  const saveCompany = async () => {
    setBusy(true);
    try {
      const current = await api.get("/company-profile").catch(() => ({ data: {} }));
      const cur = current.data || {};
      const payload = {
        // Preserve fields owned by Company Profile page
        dba: cur.dba || "", website: cur.website || "",
        address: cur.address || { line1: "", line2: "", city: "", state: "", zip: "" },
        license_number: cur.license_number || "", license_state: cur.license_state || "",
        license_expires: cur.license_expires || "",
        brand_primary_hex: cur.brand_primary_hex || "", brand_accent_hex: cur.brand_accent_hex || "",
        // Owned by this section:
        company_name: company.company_name,
        primary_trade: company.primary_trade,
        trade_categories: company.trade_categories,
        phone: company.phone, email: company.email,
        service_area_notes: company.service_area_notes,
        time_zone: company.time_zone || DEFAULT_TIME_ZONE,
      };
      await api.put("/company-profile", payload);
      toast.success(t("onboarding.saved"));
      gotoNext();
    } catch (err) { toast.error(explainSaveError(err, "Company Basics")); }
    finally { setBusy(false); }
  };

  const saveTrades = async () => {
    setBusy(true);
    try {
      const current = await api.get("/company-profile").catch(() => ({ data: {} }));
      const cur = current.data || {};
      const payload = {
        ...cur,
        primary_trade: company.primary_trade,
        trade_categories: company.trade_categories,
      };
      delete payload.id; delete payload.user_id; delete payload.updated_at;
      await api.put("/company-profile", payload);
      toast.success("Trades saved");
      gotoNext();
    } catch (err) { toast.error(explainSaveError(err, "Trades")); }
    finally { setBusy(false); }
  };

  const savePricingBasics = async () => {
    setBusy(true);
    try {
      const current = await api.get("/pricing-profile");
      const payload = {
        ...current.data,
        default_pricing_style: pricing.default_pricing_style,
        labor_rate: Number(pricing.labor_rate) || 0,
        markup_pct: Number(pricing.markup_pct) || 0,
        profit_margin_pct: Number(pricing.profit_margin_pct) || 0,
        tax_rate: Number(pricing.tax_rate) || 0,
        trip_charge: Number(pricing.trip_charge) || 0,
        deposit_pct: Number(pricing.deposit_pct) || 0,
      };
      delete payload.id; delete payload.user_id; delete payload.updated_at;
      await api.put("/pricing-profile", payload);
      toast.success(t("onboarding.saved"));
      gotoNext();
    } catch (err) { toast.error(explainSaveError(err, "Pricing Basics")); }
    finally { setBusy(false); }
  };

  const saveTradePricing = async () => {
    setBusy(true);
    try {
      const current = await api.get("/pricing-profile");
      const payload = {
        ...current.data,
        material_rules: pricing.material_rules || [],
      };
      delete payload.id; delete payload.user_id; delete payload.updated_at;
      await api.put("/pricing-profile", payload);
      toast.success(t("onboarding.saved"));
      // Re-fetch onboarding so readiness reflects any newly priced rules.
      try { const ob = await api.get("/onboarding-status"); setOnboarding(ob.data); } catch { /* non-fatal */ }
      gotoNext();
    } catch (err) { toast.error(explainSaveError(err, "Trade / System Pricing")); }
    finally { setBusy(false); }
  };

  const saveDocuments = async () => {
    setBusy(true);
    try {
      const existing = await api.get("/document-defaults").catch(() => ({ data: {} }));
      const payload = { ...existing.data, ...documents };
      delete payload.id; delete payload.user_id; delete payload.updated_at;
      await api.put("/document-defaults", payload);
      toast.success(t("onboarding.saved"));
      gotoNext();
    } catch (err) { toast.error(explainSaveError(err, "Document Defaults")); }
    finally { setBusy(false); }
  };

  const saveCloseout = async () => {
    setBusy(true);
    try {
      const existing = await api.get("/closeout-defaults").catch(() => ({ data: {} }));
      const payload = { ...existing.data, ...closeout };
      delete payload.id; delete payload.user_id; delete payload.updated_at;
      await api.put("/closeout-defaults", payload);
      toast.success(t("onboarding.saved"));
      gotoNext();
    } catch (err) { toast.error(explainSaveError(err, "Closeout Defaults")); }
    finally { setBusy(false); }
  };

  const saveAndExit = async () => {
    // Persist the current section pointer, then go to Dashboard.
    await persistSection(section);
    navigate("/app");
  };

  // ---------- Section status (real, backed by onboarding-status) ----------
  const sectionStatus = useMemo(() => {
    const ob = onboarding || {};
    const tradesDone = (company.trade_categories || []).length > 0 || !!company.primary_trade;
    return {
      company: !!ob.company_profile_complete,
      trades: tradesDone,
      "pricing-basics": !!ob.pricing_profile_complete,
      // Trade / System Pricing status uses the iter54 readiness evaluator.
      "trade-pricing": !!ob.pricing_ready,
      documents: !!ob.document_defaults_complete,
      closeout: !!ob.closeout_defaults_complete,
      review: !!ob.is_complete && !!ob.pricing_ready,
    };
  }, [onboarding, company.trade_categories, company.primary_trade]);

  const completedCount = SECTIONS.filter((s) => sectionStatus[s.id]).length;

  // ---------- Rendering ----------
  if (loading) {
    return (
      <DashboardLayout title="Contractor Setup Wizard" subtitle="Let’s set up JobClose AI around the way you run your business.">
        <div className="flex items-center gap-2 text-sm text-[var(--jc-text-soft)]" data-testid="setup-loading">
          <Loader2 className="animate-spin" size={16} /> Loading…
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title={t("onboarding.step_business_trades")}
      subtitle={t("onboarding.welcome_body")}
      actions={
        <button
          onClick={saveAndExit}
          data-testid="setup-save-and-exit-btn"
          className="text-sm font-semibold text-[var(--jc-text-soft)] hover:text-[var(--jc-text)] inline-flex items-center gap-1"
        >
          Save &amp; Exit <ArrowRight size={12} />
        </button>
      }
    >
      <div className="max-w-6xl" data-testid="setup-page">
        <div className="mb-3 flex items-center gap-2">
          <span
            data-testid="setup-progress-label"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-full bg-[var(--jc-navy)]/10 text-[var(--jc-navy)]"
          >
            {completedCount} of {SECTIONS.length} sections complete
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-6" data-testid="setup-layout">
          {/* Sidebar / stepper */}
          <nav
            aria-label="Setup sections"
            data-testid="setup-stepper"
            className="jc-card p-2 md:sticky md:top-4 h-fit"
          >
            <ol className="space-y-1">
              {SECTIONS.map((s, i) => {
                const isActive = s.id === section;
                const isDone = !!sectionStatus[s.id];
                const Icon = s.icon;
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => goto(s.id)}
                      data-testid={`setup-nav-${s.id}`}
                      aria-current={isActive ? "step" : undefined}
                      className={`w-full flex items-center gap-2 px-2.5 py-2 rounded text-left text-sm ${
                        isActive
                          ? "bg-[var(--jc-navy)] text-white"
                          : "text-[var(--jc-text)] hover:bg-[var(--jc-bg)]"
                      }`}
                    >
                      <span
                        className={`shrink-0 w-6 h-6 rounded-full inline-flex items-center justify-center text-[10px] font-bold ${
                          isDone
                            ? "bg-emerald-500 text-white"
                            : isActive
                            ? "bg-white text-[var(--jc-navy)]"
                            : "bg-[var(--jc-bg)] text-[var(--jc-text-soft)]"
                        }`}
                      >
                        {isDone ? <Check size={12} /> : i + 1}
                      </span>
                      <Icon size={14} className={isActive ? "" : "text-[var(--jc-text-soft)]"} />
                      <span className="truncate">{s.title}</span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </nav>

          {/* Section content */}
          <div className="jc-card p-5 md:p-6" data-testid={`setup-section-${section}`}>
            {section === "company" && (
              <div className="space-y-4" data-testid="setup-section-company-body">
                <div className="overline">Section 1 · Company Basics</div>
                <h2 className="font-display text-2xl">Tell us about your business.</h2>
                <p className="text-sm text-[var(--jc-text-soft)] max-w-2xl">
                  This shows up on customer-facing proposals, contracts, and invoices. You can polish the rest (address, license, brand) any time in{" "}
                  <Link to="/app/company-profile" className="underline">Company Profile</Link>.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <FieldLabel>Company name *</FieldLabel>
                    <TextInput
                      value={company.company_name}
                      onChange={(e) => setCompany({ ...company, company_name: e.target.value })}
                      placeholder="Your company"
                      data-testid="setup-company-name-input"
                    />
                  </div>
                  <div>
                    <FieldLabel>Primary trade / service</FieldLabel>
                    <TextInput
                      value={company.primary_trade}
                      onChange={(e) => setCompany({ ...company, primary_trade: e.target.value })}
                      placeholder="e.g. Roofing, Flooring, General Contractor"
                      data-testid="setup-company-primary-trade-input"
                    />
                  </div>
                  <div>
                    <FieldLabel>Phone</FieldLabel>
                    <TextInput
                      value={company.phone}
                      onChange={(e) => setCompany({ ...company, phone: e.target.value })}
                      placeholder="(555) 555-5555"
                      data-testid="setup-company-phone-input"
                    />
                  </div>
                  <div>
                    <FieldLabel>Contact email</FieldLabel>
                    <TextInput
                      value={company.email}
                      onChange={(e) => setCompany({ ...company, email: e.target.value })}
                      placeholder="office@company.com"
                      data-testid="setup-company-email-input"
                    />
                  </div>
                  <div>
                    <FieldLabel>Time zone</FieldLabel>
                    <select
                      value={company.time_zone || DEFAULT_TIME_ZONE}
                      onChange={(e) => setCompany({ ...company, time_zone: e.target.value })}
                      data-testid="setup-company-tz-select"
                      className="w-full px-3.5 py-2.5 rounded-md border border-[var(--jc-border-strong)]"
                    >
                      {TIME_ZONE_OPTIONS.map((tz) => (
                        <option key={tz.value} value={tz.value}>{tz.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <FieldLabel>Service area (optional)</FieldLabel>
                    <TextInput
                      value={company.service_area_notes}
                      onChange={(e) => setCompany({ ...company, service_area_notes: e.target.value })}
                      placeholder="e.g. San Antonio and surrounding 50 mi"
                      data-testid="setup-company-service-area-input"
                    />
                  </div>
                </div>
                <SectionNav busy={busy} onBack={null} onSave={saveCompany}
                  saveDisabled={!company.company_name.trim()}
                  saveTestId="setup-company-save-btn" />
              </div>
            )}

            {section === "trades" && (
              <div className="space-y-4" data-testid="setup-section-trades-body">
                <div className="overline">Section 2 · Trades</div>
                <h2 className="font-display text-2xl">What trades do you cover?</h2>
                <p className="text-sm text-[var(--jc-text-soft)] max-w-2xl">
                  Pick every trade your crew handles — JobClose AI uses these to pull the right pricing rules and Levi advisory hints.
                </p>
                <div className="flex flex-wrap gap-2" data-testid="setup-trades-chips">
                  {tradeChoices.map((label) => {
                    const isOn = company.trade_categories.includes(label);
                    return (
                      <button
                        key={label}
                        type="button"
                        onClick={() => {
                          setCompany((prev) => ({
                            ...prev,
                            trade_categories: isOn
                              ? prev.trade_categories.filter((t) => t !== label)
                              : [...prev.trade_categories, label],
                          }));
                        }}
                        data-testid={`setup-trade-chip-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
                          isOn
                            ? "bg-[var(--jc-navy)] text-white border-[var(--jc-navy)]"
                            : "bg-white text-[var(--jc-text)] border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                <SectionNav busy={busy} onBack={gotoPrev} onSave={saveTrades}
                  saveTestId="setup-trades-save-btn" />
              </div>
            )}

            {section === "pricing-basics" && (
              <PricingBasicsForm pricing={pricing} setPricing={setPricing}
                busy={busy} onBack={gotoPrev} onSave={savePricingBasics} />
            )}

            {section === "trade-pricing" && (
              <TradePricingSection
                pricing={pricing} setPricing={setPricing}
                onboarding={onboarding}
                trades={company.trade_categories}
                busy={busy} onBack={gotoPrev} onSave={saveTradePricing}
              />
            )}

            {section === "documents" && (
              <DocumentDefaultsForm documents={documents} setDocuments={setDocuments}
                busy={busy} onBack={gotoPrev} onSave={saveDocuments} />
            )}

            {section === "closeout" && (
              <CloseoutDefaultsForm closeout={closeout} setCloseout={setCloseout}
                busy={busy} onBack={gotoPrev} onSave={saveCloseout} />
            )}

            {section === "review" && (
              <ReadinessReview
                onboarding={onboarding}
                sectionStatus={sectionStatus}
                goto={goto}
              />
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

// ---------- Section footer (Back / Save & Continue) ----------
const SectionNav = ({ busy, onBack, onSave, saveDisabled, saveLabel = "Save & Continue", saveTestId }) => (
  <div className="mt-6 flex items-center justify-between gap-3" data-testid="setup-section-nav">
    <button
      type="button"
      onClick={onBack || undefined}
      disabled={!onBack || busy}
      data-testid="setup-back-btn"
      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)] disabled:opacity-40"
    >
      <ArrowLeft size={14} /> Back
    </button>
    <button
      type="button"
      onClick={onSave}
      disabled={busy || saveDisabled}
      data-testid={saveTestId || "setup-save-btn"}
      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-60"
    >
      {busy ? <Loader2 className="animate-spin" size={14} /> : null}
      {saveLabel} <ArrowRight size={14} />
    </button>
  </div>
);

// ---------- Pricing Basics section ----------
const PricingBasicsForm = ({ pricing, setPricing, busy, onBack, onSave }) => (
  <div className="space-y-4" data-testid="setup-section-pricing-basics-body">
    <div className="overline">Section 3 · Pricing Basics</div>
    <h2 className="font-display text-2xl">Set your company-wide defaults.</h2>
    <p className="text-sm text-[var(--jc-text-soft)] max-w-2xl">
      These are the numbers JobClose AI uses when a line item doesn&apos;t override them. You can revise anytime under{" "}
      <Link to="/app/pricing-profile" className="underline">Pricing Settings</Link>.
    </p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <FieldLabel hint="How you usually price jobs — materials, labor, overhead, and profit are already included in All-in prices. Not limited to square footage — works for square, sq ft, linear ft, fixture, opening, item, and more.">
          How do you normally price your jobs?
        </FieldLabel>
        <select
          value={pricing.default_pricing_style}
          onChange={(e) => setPricing({ ...pricing, default_pricing_style: e.target.value })}
          data-testid="setup-pricing-style-select"
          className="w-full px-3.5 py-2.5 rounded-md border border-[var(--jc-border-strong)]"
        >
          <option value="unit">All-in Price by Unit (Recommended)</option>
          <option value="flat">All-in Price by Job or System</option>
          <option value="learn_from_history">Learn From My Approved Estimates</option>
          <option value="help_me_setup">Other / Help Me Set It Up</option>
        </select>
        {(pricing.default_pricing_style === "learn_from_history" || pricing.default_pricing_style === "help_me_setup") && (
          <p
            className="mt-2 text-xs text-[var(--jc-text-soft)]"
            data-testid="setup-pricing-style-continue-hint"
          >
            {pricing.default_pricing_style === "learn_from_history"
              ? "Levi can review your approved past estimates and suggest pricing on the "
              : "Levi routes you into the existing advanced pricing setup on the "}
            <Link to="/app/pricing-profile" className="underline">Pricing Settings page</Link>
            {pricing.default_pricing_style === "learn_from_history"
              ? ". You review and approve every price before it becomes a rule."
              : ". You stay in control of every price."}
          </p>
        )}
      </div>
      <div>
        <FieldLabel>Labor rate</FieldLabel>
        <NumberInput
          value={pricing.labor_rate}
          onChange={(e) => setPricing({ ...pricing, labor_rate: e.target.value })}
          suffix="$/hr"
          data-testid="setup-labor-rate-input"
        />
      </div>
      <div>
        <FieldLabel>Markup %</FieldLabel>
        <NumberInput
          value={pricing.markup_pct}
          onChange={(e) => setPricing({ ...pricing, markup_pct: e.target.value })}
          suffix="%"
          data-testid="setup-markup-pct-input"
        />
      </div>
      <div>
        <FieldLabel>Profit margin %</FieldLabel>
        <NumberInput
          value={pricing.profit_margin_pct}
          onChange={(e) => setPricing({ ...pricing, profit_margin_pct: e.target.value })}
          suffix="%"
          data-testid="setup-profit-margin-input"
        />
      </div>
      <div>
        <FieldLabel>Sales tax rate</FieldLabel>
        <NumberInput
          value={pricing.tax_rate}
          onChange={(e) => setPricing({ ...pricing, tax_rate: e.target.value })}
          suffix="%"
          data-testid="setup-tax-rate-input"
        />
      </div>
      <div>
        <FieldLabel>Default deposit %</FieldLabel>
        <NumberInput
          value={pricing.deposit_pct}
          onChange={(e) => setPricing({ ...pricing, deposit_pct: e.target.value })}
          suffix="%"
          data-testid="setup-deposit-pct-input"
        />
      </div>
    </div>
    <SectionNav busy={busy} onBack={onBack} onSave={onSave} saveTestId="setup-pricing-basics-save-btn" />
  </div>
);

// ---------- Trade / System Pricing section ----------
const emptyRule = {
  trade: "", system_type: "", name: "",
  pricing_type: "unit_priced",
  unit: "square", material_basis_unit: "each", material_qty_per_unit: 1,
  material_unit_cost: 0,
  labor_basis: "hour", labor_qty_per_unit: 1, labor_unit_cost: 0,
  material_required: true,
  needs_pricing: false,
};

const TradePricingSection = ({ pricing, setPricing, onboarding, trades, busy, onBack, onSave }) => {
  const { t } = useLang();
  const [starterCatalog, setStarterCatalog] = useState([]);
  const [selectedStarters, setSelectedStarters] = useState(new Set());
  const [confirmReload, setConfirmReload] = useState(null); // { label } | null
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [loadingStarter, setLoadingStarter] = useState(false);
  const rules = pricing.material_rules || [];
  const roofingSelected = (trades || []).includes("Roofing");

  useEffect(() => {
    if (!roofingSelected) return;
    api.get("/pricing-profile/roofing-starter-systems")
      .then((r) => setStarterCatalog(r.data?.systems || []))
      .catch(() => setStarterCatalog([]));
  }, [roofingSelected]);

  // Which starter labels are already loaded in the contractor's profile?
  const loadedLabels = useMemo(() => {
    const labels = new Set();
    for (const r of rules) {
      if (r?.system_type) labels.add(r.system_type);
    }
    return labels;
  }, [rules]);

  // Rules grouped by system_type for the pricing panel below the picker.
  const rulesBySystem = useMemo(() => {
    const groups = {};
    for (const r of rules) {
      const key = r?.system_type || r?.trade || "Other";
      (groups[key] ||= []).push(r);
    }
    return groups;
  }, [rules]);

  const toggleStarter = (label) => {
    const next = new Set(selectedStarters);
    if (next.has(label)) next.delete(label); else next.add(label);
    setSelectedStarters(next);
  };

  const runLoadStarter = async (label, mode) => {
    setLoadingStarter(true);
    try {
      const { data } = await api.post("/pricing-profile/load-roofing-starter", { label, mode });
      // Refresh material_rules from the authoritative profile response.
      const rulesOut = data.profile?.material_rules || [];
      setPricing((prev) => ({ ...prev, material_rules: rulesOut }));
      const msg = mode === "replace"
        ? `Reloaded ${data.added} rows for ${label}.`
        : (data.skipped_priced > 0
            ? `Added ${data.added} new rows. Kept ${data.skipped_priced} priced row(s) you'd already entered.`
            : `Added ${data.added} starter rows for ${label}.`);
      toast.success(msg);
    } catch { toast.error(`Could not load starter for ${label}`); }
    finally { setLoadingStarter(false); }
  };

  const loadSelected = async () => {
    if (selectedStarters.size === 0) {
      toast.info("Pick a roofing system first, then load starter rows.");
      return;
    }
    // Merge-mode adds only rows the contractor doesn't already have.
    for (const label of selectedStarters) {
      await runLoadStarter(label, "merge");
    }
    setSelectedStarters(new Set());
  };

  const requestReload = (label) => setConfirmReload({ label });
  const doReload = async () => {
    if (!confirmReload) return;
    await runLoadStarter(confirmReload.label, "replace");
    setConfirmReload(null);
  };

  const addManualRule = () => {
    const nextTrade = (trades || [])[0] || "";
    setPricing({
      ...pricing,
      material_rules: [...rules, { ...emptyRule, trade: nextTrade }],
    });
    setShowAdvanced(true);
  };
  const removeRule = (idx) => {
    if (!window.confirm("Remove this pricing rule?")) return;
    const next = clone(rules); next.splice(idx, 1);
    setPricing({ ...pricing, material_rules: next });
  };
  const updateRule = (idx, patch) => {
    const next = clone(rules);
    next[idx] = { ...next[idx], ...patch, needs_pricing: false };
    // A row is only "priced" once material_unit_cost > 0 OR labor_unit_cost > 0.
    const m = Number(next[idx].material_unit_cost) || 0;
    const l = Number(next[idx].labor_unit_cost) || 0;
    next[idx].needs_pricing = !(m > 0 || l > 0);
    setPricing({ ...pricing, material_rules: next });
  };

  return (
    <div className="space-y-5" data-testid="setup-section-trade-pricing-body">
      <div className="overline">Section 4 · Trade / System Pricing</div>
      <h2 className="font-display text-2xl" data-testid="setup-trade-pricing-headline">
        {roofingSelected
          ? "Which roofing systems do you install?"
          : "Choose the systems you install."}
      </h2>
      <p className="text-sm text-[var(--jc-text-soft)] max-w-2xl" data-testid="setup-trade-pricing-blurb">
        Choose the systems you install, then enter your own pricing. You can update these settings later without rebuilding your setup.
      </p>

      {/* PO 2026-02 · Pricing completeness clarity — reassures the
          contractor that unused systems don't block setup. Copy-only,
          no schema, no logic changes. */}
      <div
        data-testid="setup-trade-pricing-optional-helper"
        className="text-xs text-[var(--jc-text-soft)] border-l-4 border-l-sky-400 bg-sky-50/50 px-3 py-2 rounded"
      >
        {t("soyb.systems_optional_helper")}
      </div>

      {onboarding?.pricing_ready === false && (
        <div
          data-testid="setup-trade-pricing-readiness-warn"
          className="border-l-4 border-l-amber-400 bg-amber-50/50 p-3 rounded flex items-start gap-2"
        >
          <AlertTriangle size={16} className="text-amber-700 mt-0.5 shrink-0" />
          <div className="text-xs text-amber-900">
            <div className="font-semibold text-sm">Account pricing setup is incomplete</div>
            {(onboarding.pricing_readiness_reasons || []).slice(0, 4).map((r, i) => (
              <div key={i}>• {r}</div>
            ))}
          </div>
        </div>
      )}

      {/* ---------- Roofing starter picker ---------- */}
      {roofingSelected ? (
        <div className="space-y-3" data-testid="setup-roofing-starter-picker">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {starterCatalog.map((s) => {
              const isSelected = selectedStarters.has(s.label);
              const isLoaded = loadedLabels.has(s.system_type);
              const testKey = s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-");
              return (
                <button
                  key={s.label}
                  type="button"
                  onClick={() => toggleStarter(s.label)}
                  disabled={loadingStarter}
                  data-testid={`setup-roofing-starter-${testKey}`}
                  data-selected={isSelected ? "true" : "false"}
                  data-loaded={isLoaded ? "true" : "false"}
                  className={`text-left px-3 py-2 rounded border ${
                    isSelected
                      ? "border-[var(--jc-navy)] bg-[var(--jc-navy)]/5"
                      : "border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <span
                      className={`shrink-0 mt-0.5 w-4 h-4 rounded border ${
                        isSelected
                          ? "bg-[var(--jc-navy)] border-[var(--jc-navy)]"
                          : "border-[var(--jc-border-strong)]"
                      } flex items-center justify-center`}
                    >
                      {isSelected && <Check size={10} className="text-white" />}
                    </span>
                    <div className="flex-1">
                      <div className="text-sm font-semibold">{s.label}</div>
                      <div className="text-[11px] text-[var(--jc-text-soft)]">
                        {s.row_count} scope items · {isLoaded ? "Loaded" : "Not yet loaded"}
                      </div>
                    </div>
                    {isLoaded && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); requestReload(s.label); }}
                        data-testid={`setup-roofing-starter-${testKey}-reload`}
                        className="text-[11px] text-[var(--jc-red)] hover:underline"
                      >
                        Reload…
                      </button>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={loadSelected}
              disabled={loadingStarter || selectedStarters.size === 0}
              data-testid="setup-load-starter-rows-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-50"
            >
              {loadingStarter ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />} Load Starter Pricing Rows
            </button>
            <span className="text-xs text-[var(--jc-text-soft)]">
              Starter rows carry no pricing — you enter your own labor and material costs below.
            </span>
          </div>
        </div>
      ) : (
        <div className="text-sm text-[var(--jc-text-soft)] p-3 border border-dashed border-[var(--jc-border-strong)] rounded" data-testid="setup-no-roofing-selected">
          Add <strong>Roofing</strong> to your trades in Section 2 to see the roofing starter systems. Other trades can be added manually below.
        </div>
      )}

      {/* ---------- Loaded rows grouped by system ---------- */}
      <div className="space-y-3" data-testid="setup-loaded-systems">
        {Object.keys(rulesBySystem).length === 0 && (
          <div className="text-sm text-[var(--jc-text-soft)] p-3 border border-dashed border-[var(--jc-border-strong)] rounded" data-testid="setup-no-rules-hint">
            No pricing rows yet. Pick a roofing system above and load its starter rows.
          </div>
        )}
        {Object.entries(rulesBySystem).map(([sys, group]) => {
          const groupKey = sys.toLowerCase().replace(/[^a-z0-9]+/g, "-");
          const priced = group.filter((r) =>
            (Number(r.material_unit_cost) || 0) > 0 || (Number(r.labor_unit_cost) || 0) > 0
          ).length;
          const total = group.length;
          return (
            <details
              key={sys}
              open
              className="border border-[var(--jc-border-strong)] rounded"
              data-testid={`setup-system-group-${groupKey}`}
            >
              <summary className="cursor-pointer px-3 py-2 flex items-center justify-between text-sm font-semibold bg-[var(--jc-bg)] rounded-t">
                <span>{sys}</span>
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${
                  priced === total
                    ? "bg-emerald-500 text-white"
                    : "bg-amber-100 text-amber-800"
                }`} data-testid={`setup-system-status-${groupKey}`}>
                  {priced}/{total} priced
                </span>
              </summary>
              <div className="p-3 space-y-3">
                {group.map((r) => {
                  const idx = rules.indexOf(r);
                  const isPriced = (Number(r.material_unit_cost) || 0) > 0 || (Number(r.labor_unit_cost) || 0) > 0;
                  return (
                    <div key={r.id || idx} className="p-2 border border-[var(--jc-border)] rounded" data-testid={`setup-rule-${idx}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold" data-testid={`setup-rule-${idx}-name`}>
                            {r.name || <span className="text-[var(--jc-text-soft)]">(unnamed scope item)</span>}
                          </div>
                          {r.description && (
                            <div className="text-[11px] text-[var(--jc-text-soft)]">{r.description}</div>
                          )}
                        </div>
                        <span
                          className={`text-[11px] font-semibold px-2 py-0.5 rounded shrink-0 ${
                            isPriced ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
                          }`}
                          data-testid={`setup-rule-${idx}-status`}
                        >
                          {isPriced ? "Priced" : "Contractor pricing required"}
                        </span>
                      </div>
                      <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
                        <div>
                          <FieldLabel>Unit</FieldLabel>
                          <TextInput
                            value={r.unit || ""}
                            onChange={(e) => updateRule(idx, { unit: e.target.value })}
                            data-testid={`setup-rule-${idx}-unit`}
                          />
                        </div>
                        <div>
                          <FieldLabel>Labor price</FieldLabel>
                          <NumberInput
                            value={r.labor_unit_cost ?? 0}
                            onChange={(e) => updateRule(idx, { labor_unit_cost: Number(e.target.value) })}
                            suffix="$"
                            data-testid={`setup-rule-${idx}-labor-cost`}
                          />
                        </div>
                        <div>
                          <FieldLabel>Material price</FieldLabel>
                          <NumberInput
                            value={r.material_unit_cost ?? 0}
                            onChange={(e) => updateRule(idx, { material_unit_cost: Number(e.target.value) })}
                            suffix="$"
                            data-testid={`setup-rule-${idx}-material-cost`}
                          />
                        </div>
                        <div className="flex items-end">
                          <button
                            type="button"
                            onClick={() => removeRule(idx)}
                            data-testid={`setup-rule-${idx}-remove`}
                            className="inline-flex items-center gap-1 text-xs text-[var(--jc-red)] hover:underline"
                          >
                            <Trash2 size={12} /> Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </details>
          );
        })}
      </div>

      {/* ---------- Reload confirmation ---------- */}
      {confirmReload && (
        <div
          data-testid="setup-reload-confirm-dialog"
          className="border-l-4 border-l-rose-500 bg-rose-50/50 p-3 rounded"
        >
          <div className="text-sm font-semibold text-rose-900">
            Replace all starter rows for &quot;{confirmReload.label}&quot;?
          </div>
          <p className="text-xs text-rose-900 mt-1">
            This removes the current rows for this system (including any prices you&apos;ve entered) and loads fresh starter rows. Continue?
          </p>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={() => setConfirmReload(null)}
              data-testid="setup-reload-cancel-btn"
              className="px-3 py-1.5 text-xs font-semibold rounded border border-[var(--jc-border-strong)]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={doReload}
              data-testid="setup-reload-confirm-btn"
              className="px-3 py-1.5 text-xs font-semibold rounded bg-[var(--jc-red)] text-white"
            >
              Yes, replace rows
            </button>
          </div>
        </div>
      )}

      {/* ---------- Advanced: manual custom rule (collapsed) ---------- */}
      <details
        className="border border-dashed border-[var(--jc-border-strong)] rounded"
        data-testid="setup-advanced-custom-rule"
        open={showAdvanced}
      >
        <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-[var(--jc-text-soft)]" data-testid="setup-advanced-custom-rule-toggle">
          Advanced: Add a custom pricing rule
        </summary>
        <div className="p-3">
          <p className="text-xs text-[var(--jc-text-soft)] mb-2">
            Most contractors don&apos;t need this — the roofing starter systems above cover typical scopes. Use this to add a specialty rule that doesn&apos;t belong to any starter system.
          </p>
          <button
            type="button"
            onClick={addManualRule}
            data-testid="setup-add-rule-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
          >
            <Plus size={12} /> Add custom rule
          </button>
        </div>
      </details>

      <SectionNav busy={busy} onBack={onBack} onSave={onSave} saveTestId="setup-trade-pricing-save-btn" />
    </div>
  );
};

// ---------- Document / Closeout Defaults sections ----------
const DocumentDefaultsForm = ({ documents, setDocuments, busy, onBack, onSave }) => {
  const bind = (k) => ({
    value: documents[k] || "",
    onChange: (e) => setDocuments({ ...documents, [k]: e.target.value }),
    "data-testid": `setup-docdef-${k.replace(/_/g, "-")}`,
  });
  return (
    <div className="space-y-4" data-testid="setup-section-documents-body">
      <div className="overline">Section 5 · Document Defaults</div>
      <h2 className="font-display text-2xl">Default wording for proposals, contracts, and invoices.</h2>
      <p className="text-sm text-[var(--jc-text-soft)] max-w-2xl">
        Applies to <em>new</em> documents only — existing proposals, contracts, and invoices are never rewritten. Deeper editing lives in{" "}
        <Link to="/app/setup/document-defaults" className="underline">Document Defaults</Link>.
      </p>
      <div className="grid grid-cols-1 gap-4">
        <div>
          <FieldLabel>Proposal intro</FieldLabel>
          <TextArea placeholder="Thank you for the opportunity to bid this project…" {...bind("proposal_intro")} />
        </div>
        <div>
          <FieldLabel>Contract terms</FieldLabel>
          <TextArea rows={5} placeholder="Terms & conditions specific to your business…" {...bind("contract_terms")} />
        </div>
        <div>
          <FieldLabel>Invoice notes</FieldLabel>
          <TextArea placeholder="Payments due within 15 days. Please reference invoice number on payment." {...bind("invoice_notes")} />
        </div>
      </div>
      <SectionNav busy={busy} onBack={onBack} onSave={onSave} saveTestId="setup-documents-save-btn" />
    </div>
  );
};

const CloseoutDefaultsForm = ({ closeout, setCloseout, busy, onBack, onSave }) => {
  const bind = (k) => ({
    value: closeout[k] || "",
    onChange: (e) => setCloseout({ ...closeout, [k]: e.target.value }),
    "data-testid": `setup-closeout-${k.replace(/_/g, "-")}`,
  });
  return (
    <div className="space-y-4" data-testid="setup-section-closeout-body">
      <div className="overline">Section 6 · Closeout Defaults</div>
      <h2 className="font-display text-2xl">Warranty, maintenance, and closeout language.</h2>
      <p className="text-sm text-[var(--jc-text-soft)] max-w-2xl">
        Delivered when a job is complete. Deeper editing lives in{" "}
        <Link to="/app/setup/closeout-defaults" className="underline">Closeout Defaults</Link>.
      </p>
      <div className="grid grid-cols-1 gap-4">
        <div>
          <FieldLabel>Workmanship warranty terms</FieldLabel>
          <TextArea rows={4} placeholder="We warrant our workmanship for [N] years from substantial completion…" {...bind("workmanship_warranty_terms")} />
        </div>
        <div>
          <FieldLabel>Manufacturer warranty notes</FieldLabel>
          <TextArea placeholder="Reference manufacturer warranty terms and any registration steps." {...bind("manufacturer_warranty_notes")} />
        </div>
        <div>
          <FieldLabel>Maintenance notes</FieldLabel>
          <TextArea placeholder="Recommended homeowner maintenance to keep the warranty valid." {...bind("maintenance_notes")} />
        </div>
      </div>
      <SectionNav busy={busy} onBack={onBack} onSave={onSave} saveTestId="setup-closeout-save-btn" />
    </div>
  );
};

// ---------- Readiness Review section ----------
const ReadinessReview = ({ onboarding, sectionStatus, goto }) => {
  const ob = onboarding || {};
  const allReady = !!ob.pricing_ready
    && !!sectionStatus.company
    && !!sectionStatus.trades
    && !!sectionStatus["pricing-basics"]
    && !!sectionStatus.documents
    && !!sectionStatus.closeout;
  return (
    <div className="space-y-4" data-testid="setup-section-review-body">
      <div className="overline">Section 7 · Readiness Review</div>
      <h2 className="font-display text-2xl">Your setup at a glance.</h2>
      <p className="text-sm text-[var(--jc-text-soft)] max-w-2xl">
        This uses the same readiness check that gates estimates and proposals — no invented numbers, no shortcuts.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="setup-review-grid">
        {SECTIONS.slice(0, 6).map((s) => {
          const done = !!sectionStatus[s.id];
          return (
            <div
              key={s.id}
              data-testid={`setup-review-${s.id}`}
              className={`p-3 rounded border ${done ? "border-emerald-300 bg-emerald-50/40" : "border-amber-300 bg-amber-50/40"}`}
            >
              <div className="flex items-center justify-between">
                <div className="font-semibold text-sm">{s.title}</div>
                <span
                  className={`text-[11px] font-semibold px-2 py-0.5 rounded ${
                    done ? "bg-emerald-500 text-white" : "bg-amber-500 text-white"
                  }`}
                  data-testid={`setup-review-${s.id}-status`}
                >
                  {done ? "Complete" : "Needs attention"}
                </span>
              </div>
              {!done && (
                <button
                  type="button"
                  onClick={() => goto(s.id)}
                  data-testid={`setup-review-${s.id}-open`}
                  className="mt-2 text-xs font-semibold text-[var(--jc-navy)] hover:underline inline-flex items-center gap-1"
                >
                  Open {s.title} <ArrowRight size={11} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {ob.pricing_ready === false && (
        <div
          className="mt-3 border-l-4 border-l-rose-500 bg-rose-50/60 p-3 rounded"
          data-testid="setup-review-not-ready"
        >
          <div className="text-sm font-semibold text-rose-900 flex items-center gap-2">
            <AlertTriangle size={14} /> Your account isn&apos;t ready to price estimates yet.
          </div>
          {(ob.pricing_readiness_reasons || []).length > 0 && (
            <ul className="mt-1.5 text-xs text-rose-900 list-disc list-inside space-y-0.5" data-testid="setup-review-reasons">
              {ob.pricing_readiness_reasons.slice(0, 5).map((r, i) => (<li key={i}>{r}</li>))}
            </ul>
          )}
          <button
            type="button"
            onClick={() => goto("trade-pricing")}
            data-testid="setup-review-go-trade-pricing"
            className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-rose-900 hover:underline"
          >
            Open Trade / System Pricing <ArrowRight size={11} />
          </button>
        </div>
      )}

      {allReady && (
        <div
          className="mt-3 border-l-4 border-l-emerald-500 bg-emerald-50/60 p-3 rounded flex items-start gap-2"
          data-testid="setup-review-ready"
        >
          <ShieldCheck size={16} className="text-emerald-700 mt-0.5" />
          <div className="text-sm text-emerald-900" data-testid="setup-review-ready-message">
            <span className="font-semibold" data-testid="setup-review-setup-complete-label">Setup Complete.</span>{" "}
            Your account is ready to create priced estimates.
          </div>
        </div>
      )}

      <div className="pt-2">
        <Link
          to="/app"
          data-testid="setup-review-done-link"
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md jc-btn-primary"
        >
          <Sparkles size={14} /> Back to Dashboard
        </Link>
      </div>
    </div>
  );
};

// ---------- Legacy route redirects (mounted from App.js) ----------
export const QuickStartRedirect = () => <Navigate to="/app/setup" replace />;
export const PricingWizardRedirect = () => <Navigate to="/app/setup#trade-pricing" replace />;

export default Setup;
